/**
 * Kanal yazma sözleşmesi (P3 — Hayat Su bayi paneline YAZMA hazırlığı).
 *
 * Yazmanın KENDİSİ bu pakette YOK. Bu dosya yalnız iki şeyi tanımlar:
 *
 *  1. Yazma kipi (`HAYAT_WRITE_MODES`): Gölge → Onaylı → Otonom. Kapı `hayatWriteGate` ayrıca
 *     bakacak; burada yalnız tipler ve görünen etiketler (i18n ile birlikte) sabit kalır.
 *  2. Yazılabilecek eylemler (`CHANNEL_PUSH_ACTIONS`): panelden kanal bayi paneline
 *     bildirilebilecek beş hareket. Her eylem için `continuesOpenWork` YALNIZ kural 11'in
 *     uygulamasıdır: "teslim edildi" ve "iptal" açık vardiyanın kapanışına yardım eder,
 *     diğer üçü yeni iş başlatır — askıda kiracıda bu üçü kapalıdır.
 *
 * Mimari karar kaydı: `docs/entegrasyon/iki-yonlu-tasarim-taslak.md` sonundaki
 * "MİMAR HÜKMÜ" bölümü.
 */

export const HAYAT_WRITE_MODES = ['SHADOW', 'CONFIRM', 'AUTO'] as const;
export type HayatWriteMode = (typeof HAYAT_WRITE_MODES)[number];

export const CHANNEL_PUSH_ACTIONS = [
  'CONFIRM_VEHICLE',
  'SEND_TO_VEHICLE',
  'MARK_DELIVERED',
  'CHANGE_VEHICLE',
  'CANCEL',
] as const;
export type ChannelPushAction = (typeof CHANNEL_PUSH_ACTIONS)[number];

/**
 * Her eylem için açık vardiya davranışı.
 * - `true`: eylem açık bir vardiyayı sonlandırır ya da ona doğrudan hizmet eder
 *   (teslim/iptal). Askıda kiracıda bu eylemler ENGELLENMEZ — kural 11.
 * - `false`: eylem yeni iş başlatır (atama/onay/yönlendirme). Askıda kiracıda kapalı.
 *
 * `satisfies` zorunludur: yeni bir eylem eklenirse bu tablo eksik kalırsa TypeScript derleme
 * hatası verir ve sessizce `false` (yasak) yazılmasının önüne geçer.
 */
export const CHANNEL_PUSH_ACTION_TRAITS = {
  CONFIRM_VEHICLE: { continuesOpenWork: false },
  SEND_TO_VEHICLE: { continuesOpenWork: false },
  MARK_DELIVERED: { continuesOpenWork: true },
  CHANGE_VEHICLE: { continuesOpenWork: false },
  CANCEL: { continuesOpenWork: true },
} as const satisfies Record<ChannelPushAction, { continuesOpenWork: boolean }>;

/**
 * Bir denemenin sonucu — `ChannelPushAttempt.result` kolonuna yazılan sabitler.
 *
 * Kararlar bilerek STRING kabul edildi (DB'de String + zod; ADR 2026-09-25):
 * enum migration istemesin, yeni değer eklemek paket güncellemesine kalmasın.
 *
 * Anlamlar:
 *  - SENT                — yazıcı başarıyla uyguladı.
 *  - SKIPPED_ALREADY_DONE — panelde zaten hedef durumdaydı, idempotent atlandı (yazıcı çağrılmadı).
 *  - SHADOW_ONLY         — gölge kipi: kayıt düşüldü ama panele hiçbir şey gönderilmedi.
 *  - BLOCKED             — kapı reddetti (scraper/writer kapalı, kiracı kısıtlı, eylem yok, …).
 *  - FAILED              — yazıcı net biçimde başarısız döndü.
 *  - UNCERTAIN           — yazıcı kesin konuşmadı (timeout, belirsiz yanıt); otomatik tekrar yok.
 *  - PENDING             — yazıcı çağrılmadan HEMEN önce eklenen satır; sonuç AYRI satırdır
 *                          (tablo yalnız eklenir). Arkasından sonuç satırı gelmeyen PENDING
 *                          (süreç yazma sırasında öldü) UNCERTAIN muamelesi görür.
 */
export const CHANNEL_PUSH_RESULTS = [
  'SENT',
  'SKIPPED_ALREADY_DONE',
  'SHADOW_ONLY',
  'BLOCKED',
  'FAILED',
  'UNCERTAIN',
  'PENDING',
] as const;
export type ChannelPushResult = (typeof CHANNEL_PUSH_RESULTS)[number];

/**
 * Denemeyi tetikleyen kapı — `ChannelPushAttempt.trigger` kolonu.
 *
 *  - SCHEDULER — uzlaştırıcının otomatik tiki. CONFIRM kipinde tetikleyici ASLA
 *                `SCHEDULER` olamaz; AUTO'da olur.
 *  - BUTTON    — kullanıcının "Hayat'a gönder" düğmesi. SHADOW'da bile göndermez
 *                (kayıt SHADOW_ONLY düşer) — düğmenin amacı GÖNDERMEK'tir,
 *                kip kapatmaz.
 */
export const CHANNEL_PUSH_TRIGGERS = ['SCHEDULER', 'BUTTON', 'APPROVAL'] as const;
export type ChannelPushTrigger = (typeof CHANNEL_PUSH_TRIGGERS)[number];

/**
 * BLOCKED sonucunun nedenleri (uzlaştırıcı bunları `error` kolonuna taşır). Panelde
 * kullanıcıya gösterilecek metin `t('hayatPush.reason.*')` üzerinden çözülür.
 */
export const CHANNEL_PUSH_BLOCK_REASONS = [
  'scraper-disabled',
  'writer-disabled',
  'tenant-restricted',
  'unknown-action',
  'no-action-needed',
  'no-vehicle-mapping',
  'writer-not-configured',
  'last-attempt-uncertain',
  // G1 düzeltme turu (2026-09-28): Hayat panelinde siparişin kendi durumu bizden İLERİDE.
  // Bu iki neden kayıt düşürür (sessizce yutmaz), ama tetikleyici SCHEDULER ise tekrar yazılmaz.
  'hayat-cancelled',
  'hayat-delivered',
  // Hayat kanalından GELMEYEN sipariş (MANUAL vb.). Bu kapı daha DB'ye kayıt düşmeden
  // kısa devre yapar; kayıt YOK (yanıltıcı SHADOW_ONLY yığılmasını önler).
  'not-hayat-order',
  // G1 hakem turu (2026-09-28): panelde gözlenen metin tabloda yok (null/boş dahil).
  // Panelin durumunu bilmeden yazılmaz; tablo yakalama ile genişler.
  'unknown-observed',
  // FAILED sonrası zamanlayıcı geri çekilmesi: bekleme süresi dolmadı (satır yazılmaz) ve
  // ardışık FAILED tavanı aşıldı (bir kez satır + patrona uyarı; BUTTON elle yeniden dener).
  'retry-backoff',
  'retry-limit',
  // Okuyucu hakem turu (2026-09-29): Hayat'ta Tamam'a BAŞKA araçla basılmış (gözlenen araç
  // etiketi bizim eşlememizden farklı). Üstüne yazmak yanlış aracın işini ilerletirdi.
  'hayat-vehicle-mismatch',
  // Hayat kanal tutarı işi (2026-09-29): Hayat'ta "Araç Listesinde" gözlenirken CANCEL yazılmaz —
  // sipariş bayinin araç listesine geçmiş; iptal Hayat'ta bayi eliyle yapılır.
  'hayat-dispatched',
] as const;
export type ChannelPushBlockReason = (typeof CHANNEL_PUSH_BLOCK_REASONS)[number];

/**
 * Hayat yazıcısının iç sabit reason kodları (P3 / paket G2 — gerçek yazıcı).
 *
 * Bunlar `PanelPushOutput.error` kolonuna yazılır; `ChannelPushBlockReason` DEĞİLDİR
 * (yazıcı FAILED/UNCERTAIN döndürür, BLOCKED döndürmez). BLOKE reason'larıyla karışmaması
 * için ayrı listede dururlar. Yeni kod eklenince buraya da eklenmeli — yoksa typescript
 * hata vermez ama testlerde `expected` ile eşleşmez.
 */
export const HAYAT_WRITE_REASONS = [
  // Form kurucusu (hayat-confirm-form.ts) reason'ları:
  'prompter-no-pending',
  'prompter-other-order',
  'prompter-multiple',
  'prompter-malformed',
  'vehicle-option-missing',
  'delivery-time-missing',
  // Yazıcı (hayat-panel-writer.ts) reason'ları:
  'write-format-unknown',
  'prompter-get-failed',
  'confirm-post-failed',
  'confirm-server-error',
  'confirm-rejected',
  'confirm-not-verified',
  'verify-get-failed',
  // Oturum paylaşımı (paket G2, düzeltme turu): kanal-fetch'in açtığı hesap düşmüş
  // ya da daha açılmamış. Çekim servisi bir sonraki turda yeniler.
  'session-expired',
  'session-not-ready',
  // Hakem K1–U5 düzeltme turu (2026-09-29). POST ÖNCESİ (FAILED, istek gitmedi):
  'orders-read-failed',
  'target-row-missing',
  'target-vehicle-changed',
  'target-already-confirmed',
  'live-access-blocked',
  'confirm-url-foreign',
  // POST SONRASI (UNCERTAIN, panelde ne olduğu bilinmiyor):
  'verify-session-expired',
  'other-row-changed',
] as const;
export type HayatWriteReason = (typeof HAYAT_WRITE_REASONS)[number];
